import { HtmlHTMLAttributes } from 'react';

import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { FiPlay, FiUsers } from 'react-icons/fi';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { ptBR } from 'date-fns/locale';

import { RoomDataWithSpeakers } from '@common/types/api';
import { formatRoomDisplayData } from '@utils';
import { ComponentPropsWithLoading } from '@common/types';
import { SpeakersBubbles } from '@components';

import * as S from './styles';

// ── Constants ───────────────────────────────────────────

export const ROOM_COLORS = [
  '#6C63FF',
  '#2DD4A8',
  '#F472B6',
  '#FBBF24',
  '#3B82F6',
  '#EF4444',
];

const WAVE_DELAYS = [0, 0.15, 0.3, 0.1, 0.25, 0.4, 0.05, 0.2];

export const getRoomColor = (id: number) =>
  ROOM_COLORS[id % ROOM_COLORS.length];

export const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();

const TYPE_ROOM_COLORS: Record<string, string> = {
  Plenária: '#6C63FF',
  Ação: '#2DD4A8',
  Painel: '#F472B6',
  Palestra: '#FBBF24',
  Reunião: '#3B82F6',
  'Bate Papo': '#EF4444',
};

export const getTypeRoomColor = (type: string) =>
  TYPE_ROOM_COLORS[type] || '#6C63FF';

const getVariant = (
  isOnline: boolean,
  status: string,
): 'live' | 'upcoming' | 'past' => {
  if (isOnline) return 'live';
  if (status === 'pages.rooms.soon') return 'upcoming';
  return 'past';
};

// ── SVG Pattern (for gradient-only cards) ───────────────

const SvgPatternOverlay = () => (
  <S.SvgPattern
    width="100%"
    height="100%"
    viewBox="0 0 400 260"
    preserveAspectRatio="xMidYMid slice"
  >
    <circle
      cx="320"
      cy="130"
      r="160"
      fill="none"
      stroke="#fff"
      strokeWidth="0.8"
    />
    <circle
      cx="320"
      cy="130"
      r="100"
      fill="none"
      stroke="#fff"
      strokeWidth="0.5"
    />
    <circle cx="320" cy="130" r="40" fill="#fff" opacity="0.03" />
    <path
      d="M0 200 Q100 150 200 180 T400 160"
      fill="none"
      stroke="#fff"
      strokeWidth="0.6"
    />
    <path
      d="M0 100 Q80 60 160 90 T320 70 T400 90"
      fill="none"
      stroke="#fff"
      strokeWidth="0.4"
    />
  </S.SvgPattern>
);

// ── Component ───────────────────────────────────────────

type RoomCardProps = ComponentPropsWithLoading<RoomDataWithSpeakers> &
  HtmlHTMLAttributes<HTMLDivElement>;

const RoomCard = (props: RoomCardProps) => {
  const { data, loading: _ = false, ...attrs } = props;
  const { t } = useTranslation();

  // Skeleton
  if (!data) {
    return (
      <S.CardContainer $active={false} {...attrs}>
        <S.SkeletonVisual />
        <S.SkeletonInfo>
          <Skeleton width="70%" height="1rem" style={{ marginBottom: 6 }} />
          <Skeleton width="50%" height="0.75rem" style={{ marginBottom: 12 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Skeleton width="40%" height="0.75rem" />
            <Skeleton width="25%" height="0.75rem" />
          </div>
        </S.SkeletonInfo>
      </S.CardContainer>
    );
  }

  const { isOnline, status } = formatRoomDisplayData(data);
  const hasImage = !!data.thumb_image;
  const isActive = !!data.status;
  const variant = getVariant(isOnline, status);

  return (
    <S.CardContainer $active={isActive} {...attrs}>
      <S.CardLink to={`/broadcast/${data.slug}`}>
        {/* Visual Area */}
        <S.CardVisual $hasImage={hasImage}>
          {hasImage ? (
            <>
              <S.CardImage src={data.thumb_image} alt={data.name} />
              <S.CardImageOverlay />
            </>
          ) : (
            <SvgPatternOverlay />
          )}

          {/* Status Badge */}
          <S.StatusBadge $variant={variant}>
            {variant === 'live' && <S.BadgePulseDot />}
            {t(status)}
          </S.StatusBadge>

          {/* Center Content */}
          <S.CenterContent>
            {variant === 'live' ? (
              <S.AudioWave>
                {WAVE_DELAYS.map((delay) => (
                  <S.WaveBarElement key={`wave-${delay}`} $delay={delay} />
                ))}
              </S.AudioWave>
            ) : (
              <S.DateCircle>
                <span>
                  {format(parseISO(data.start_date), 'dd MMM', {
                    locale: ptBR,
                  })}
                </span>
                <span>{format(parseISO(data.start_date), 'HH:mm')}</span>
              </S.DateCircle>
            )}
          </S.CenterContent>

          {/* Enter Button (visible on hover) */}
          <S.EnterButton>
            <button type="button">
              <FiPlay size={14} />
              {t('pages.rooms.enter')}
            </button>
          </S.EnterButton>
        </S.CardVisual>

        {/* Info */}
        <S.CardInfo>
          <S.CardInfoHeader>
            <S.CardInfoLeft>
              {data.type_room && (
                <S.TypeRoomChip $chipColor={getTypeRoomColor(data.type_room)}>
                  {data.type_room}
                </S.TypeRoomChip>
              )}
              <S.RoomName>{data.name}</S.RoomName>
            </S.CardInfoLeft>

            {data.schedules?.data?.length > 0 && (
              <S.SessionBadge>
                {data.schedules.data.length}{' '}
                {data.schedules.data.length === 1
                  ? t('pages.rooms.session')
                  : t('pages.rooms.sessions')}
              </S.SessionBadge>
            )}
          </S.CardInfoHeader>

          <S.CardFooter>
            <S.SpeakersRow>
              <SpeakersBubbles
                data={data.participants.data}
                innerKey={data.id}
              />
              {data.participants.data.length > 0 && (
                <S.SpeakersLabel>
                  {data.participants.data.length} {t('menu.speakers')}
                </S.SpeakersLabel>
              )}
            </S.SpeakersRow>

            {data.max_users > 0 && (
              <S.CapacityInfo>
                <FiUsers size={10} />
                {data.max_users} {t('pages.rooms.seats')}
              </S.CapacityInfo>
            )}
          </S.CardFooter>
        </S.CardInfo>
      </S.CardLink>
    </S.CardContainer>
  );
};

export default RoomCard;
